'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getSocket } from '@/lib/echo';
import type { ApiResponse } from '@/lib/api/types';
import type { OcrTemplateSuggestion } from '@/features/ocr-templates';

import { ocrKeys, resetSuggestionPollWindow } from '../services/ocr.queries';
import type { OcrTemplateSuggestionsResponse } from '../services/ocr.api';

interface OcrTemplateSuggestionsReadyEvent {
  registration_id: number;
  suggestions: OcrTemplateSuggestion[];
}

/**
 * Push-first delivery for template suggestions: subscribe to the
 * registration's suggestion channel and patch the query cache the instant
 * the backend job broadcasts its result — the event carries the suggestion
 * list, so no follow-up HTTP round-trip is needed. The suggestions query's
 * polling stays on as a slow safety net (fast only when the WebSocket is
 * down), so a missed broadcast still resolves.
 */
export function useTemplateSuggestionsLive(
  registrationId: number,
  enabled: boolean
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || registrationId <= 0) return;

    let cancelled = false;
    const channelName = `ocr-suggestions.${registrationId}`;

    getSocket().then((socket) => {
      if (cancelled) return;

      socket
        .private(channelName)
        .listen(
          '.ocr-template-suggestions',
          (event: OcrTemplateSuggestionsReadyEvent) => {
            const key = ocrKeys.templateSuggestions(registrationId);
            resetSuggestionPollWindow(registrationId);

            const existing =
              queryClient.getQueryData<
                ApiResponse<OcrTemplateSuggestionsResponse>
              >(key);
            if (existing) {
              queryClient.setQueryData<
                ApiResponse<OcrTemplateSuggestionsResponse>
              >(key, {
                ...existing,
                data: { status: 'ready', suggestions: event.suggestions }
              });
            } else {
              // The event raced the first POST response — refetch instead
              // of fabricating a response envelope.
              queryClient.invalidateQueries({ queryKey: key });
            }
          }
        );
    });

    return () => {
      cancelled = true;
      getSocket().then((socket) => socket.leave(channelName));
    };
  }, [enabled, registrationId, queryClient]);
}
